/** Hero, supported-agent marks, and illustrative verification evidence. */
import {
  ArrowRight,
  BadgeCheck,
  Check,
  GitCommitHorizontal,
  X,
} from 'lucide-react'
import { m } from '#/paraglide/messages'
import { Mascot } from '#/components/layout/mascot'
import { Container, CtaPair, Frame, Overline } from './mk'

// ---------------------------------------------------------------- 1. hero

export function Hero() {
  return (
    <section id="top" className="mk-hero pt-12 pb-10 lg:pt-20 lg:pb-12">
      <Container>
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center">
          <div className="flex items-center justify-center gap-3">
            <Mascot size={64} eager />
            <Overline className="max-w-[28ch] text-left sm:max-w-none">
              {m.mk_hero_overline()}
            </Overline>
          </div>
          <a
            href="#agent-platform"
            className="inline-flex max-w-full items-center gap-2 rounded-full border border-[var(--mk-border)] bg-[var(--mk-bg-muted)] px-4 py-2 text-sm leading-relaxed hover:bg-white"
            data-testid="landing-release-link"
          >
            <span>{m.mk_agent_release_link()}</span>
            <ArrowRight className="size-4 shrink-0" aria-hidden />
          </a>
          <h1 className="max-w-[23ch]">{m.mk_hero_title()}</h1>
          <p className="mk-lead max-w-[54ch]">{m.mk_hero_sub()}</p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <CtaPair
              primary={m.mk_hero_cta_primary()}
              secondary={m.mk_hero_cta_secondary()}
              secondaryHref="#build"
            />
          </div>
          {/* four verifiable facts under the CTAs (no vanity metrics): every one maps to a shipped feature */}
          <ul className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[var(--mk-fg-muted)]">
            {[
              m.mk_hero_fact_1(),
              m.mk_hero_fact_2(),
              m.mk_hero_fact_3(),
              m.mk_hero_fact_4(),
            ].map((f) => (
              <li key={f} className="inline-flex items-center gap-1.5">
                <Check className="size-3.5 text-[var(--mk-fg)]" aria-hidden />
                {f}
              </li>
            ))}
          </ul>
        </div>
        {/* plane.so hero frame: screenshot offset from the top, cropped at the bottom by the frame's max-height */}
        <Frame
          src="/landing/hero-roadmap.png"
          alt={m.mk_hero_frame_alt()}
          width={1440}
          height={900}
          tone="light"
          offset="lg"
          eager
          className="mk-hero-frame mt-10 max-h-[560px] lg:mt-14 lg:max-h-[600px]"
          // on phones plane.so lets the shot bleed off the right edge instead of shrinking to a thumbnail
          imgClassName="mk-hero-shot"
        />
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 2. agents strip (plane.so's customer-logo grid)

/**
 * Proper nouns: the agents `bystep init` installs a skill for. Not translated.
 * Brand marks live in public/landing/agents/ (backgrounds removed, trimmed, ≤320px). `wordmark` logos already
 * carry the name, so the text label is visually dropped and kept for screen readers only. Pi = pi.dev/favicon.svg.
 */
type Agent = { name: string; logo: string; wordmark?: boolean }
const AGENTS: Agent[] = [
  { name: 'Claude Code', logo: '/landing/agents/claude-code.png' },
  { name: 'Cursor', logo: '/landing/agents/cursor.png' },
  { name: 'Codex', logo: '/landing/agents/codex.png' },
  {
    name: 'GitHub Copilot',
    logo: '/landing/agents/github-copilot.png',
    wordmark: true,
  },
  { name: 'Gemini CLI', logo: '/landing/agents/gemini-cli.png' },
  { name: 'OpenCode', logo: '/landing/agents/opencode.png', wordmark: true },
  { name: 'Antigravity', logo: '/landing/agents/antigravity.png' },
  { name: 'Pi', logo: '/landing/agents/pi.svg' },
]

function AgentLogo({ logo, wordmark }: Agent) {
  return (
    <img
      src={logo}
      alt=""
      height={wordmark ? 26 : 30}
      loading="eager"
      decoding="async"
      draggable={false}
      className={wordmark ? 'mk-agent-logo mk-agent-wordmark' : 'mk-agent-logo'}
    />
  )
}

/** Static marks: supported agents, not customer endorsements. */
function AgentRow() {
  return (
    <ul>
      {AGENTS.map((a) => (
        <li key={a.name} className="mk-marquee-item" title={a.name}>
          <AgentLogo {...a} />
          <span
            className={
              a.wordmark
                ? 'sr-only'
                : 'font-heading text-lg font-semibold tracking-tight'
            }
          >
            {a.name}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function AgentsStrip() {
  return (
    <section className="pt-4 pb-16">
      <Container>
        <p className="text-center text-sm text-[var(--mk-fg-muted)]">
          {m.mk_agents_title()}
        </p>
      </Container>
      <div className="mk-agent-grid mt-7">
        <AgentRow />
      </div>
      <Container>
        <p className="mx-auto mt-10 max-w-[60ch] text-center text-sm text-[var(--mk-fg-faint)]">
          {m.mk_agents_sub()}
        </p>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------- 3b. proof of done ("Proof, not claims")

/** The four criteria of the mocked task: whatever `bystep task verify` ran, in the order the CLI prints them. */
const PROOF_ROWS: Array<() => string> = [
  () => m.mk_proof_panel_row1(),
  () => m.mk_proof_panel_row2(),
  () => m.mk_proof_panel_row3(),
  () => m.mk_proof_panel_row4(),
]

/**
 * Static mock of the evidence panel — plain Tailwind, no screenshot, so it stays legible at 390px and
 * never goes stale. Semantic status/diff tokens only (never the accent), like the rest of the app.
 */
function EvidencePanel() {
  return (
    <div
      className="mk-frame p-4 sm:p-8"
      role="img"
      aria-label={m.mk_proof_panel_alt()}
    >
      <div className="mk-card-white overflow-hidden shadow-[0_24px_60px_-20px_rgba(15,17,21,0.35)]">
        <div className="flex items-start justify-between gap-3 border-b border-[var(--mk-border)] px-4 py-4 sm:px-5">
          <p className="text-[15px] leading-snug font-medium text-[var(--mk-fg)]">
            {m.mk_proof_panel_task()}
          </p>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-status-done/30 bg-status-done/10 px-2.5 py-1 text-xs font-medium text-status-done">
            <BadgeCheck className="size-3.5" aria-hidden />
            {m.mk_proof_panel_badge()}
          </span>
        </div>
        <ul className="divide-y divide-[var(--mk-border)]">
          {PROOF_ROWS.map((row) => (
            <li
              key={row()}
              className="flex items-center gap-3 px-4 py-3 font-mono text-[13px] text-[var(--mk-fg)] sm:px-5"
            >
              <Check className="size-4 shrink-0 text-status-done" aria-hidden />
              <span className="min-w-0 break-words">{row()}</span>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3 border-t border-[var(--mk-border)] bg-[var(--mk-bg-muted)] px-4 py-3 font-mono text-[13px] text-[var(--mk-fg-muted)] sm:px-5">
          <GitCommitHorizontal className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            commit a1b2c3d · <span className="text-diff-add">+214</span>{' '}
            <span className="text-diff-remove">−37</span> · 42s
          </span>
        </div>
        <div className="flex items-center gap-3 border-t border-[var(--mk-border)] px-4 py-3 font-mono text-[13px] text-status-failed sm:px-5">
          <X className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">
            {m.mk_proof_panel_rejected()}
          </span>
        </div>
      </div>
    </div>
  )
}

export function Proof() {
  return (
    <section id="proof" className="scroll-mt-20 pb-24 lg:pb-32">
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col items-start gap-5">
            <Overline>{m.mk_proof_overline()}</Overline>
            <h2 className="max-w-[16ch]">{m.mk_proof_title()}</h2>
            <ul className="mt-2 flex flex-col gap-4">
              {[m.mk_proof_1(), m.mk_proof_2(), m.mk_proof_3()].map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 text-[17px] leading-relaxed text-[var(--mk-fg-muted)]"
                >
                  <Check
                    className="mt-1.5 size-4 shrink-0 text-[var(--mk-fg)]"
                    aria-hidden
                  />
                  <span className="max-w-[46ch]">{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-3 text-xs text-[var(--mk-fg-muted)]">
              {m.mk_mock_label()}
            </p>
            <EvidencePanel />
          </div>
        </div>
      </Container>
    </section>
  )
}
